
import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { ModuleBase, GUEST_PERMISSIONS } from '@loanbossinc/component-library';
import { withRouter } from "react-router";
import { QuoteTable } from "./quoteTable";
import styled from "styled-components";

import {
  DefaultModuleHeader
  , ModuleEmptyButton
  , HomeWork
  , DEAL_STATUSES
  , LOAN_SENIORITY_TYPES
  , ButtonText
  , ButtonSizeOptions
  , MaterialIcon
  , ButtonBar
} from '@loanbossinc/component-library';
import {
  quoteModuleGetQuotes
  , quoteModuleGetSupplementalQuotes
  , quoteModuleGetModifyingQuotes
  , quoteModuleGetDeal
  , mapModulePermissions
} from './quote.store';

const StyledFooterButton = styled(ButtonText)`
  && {
    display: flex;
    flex: 1;
    justify-content: space-between;
    width: 100%;
  }
`;

const DealStatusShownOn = [DEAL_STATUSES.PIPELINE, DEAL_STATUSES.REFINANCE, DEAL_STATUSES.ADDING_SUPPLEMENTAL, DEAL_STATUSES.MODIFYING, DEAL_STATUSES.DEAD];

class QuoteModule extends React.Component {
  state = {
    seniorQuotes: []
    , subordinateQuotes: []
    , allQuotes: []
    , isViewingSeniorQuotes: true
    , shouldShowModule: false
  };

  componentDidMount() {
    const { dealId } = this.props.match.params;
    this.props.quoteModuleGetDeal({ dealId });
  }

  shouldUpdateDealStatus = (prevProps, currentProps) => {
    if (!currentProps || !currentProps.deal) return false;
    if (!prevProps.deal && !!currentProps.deal) return true;

    const prevLoanCount = prevProps.deal && prevProps.deal.loanCount ? prevProps.deal.loanCount : 0;
    const currentLoanCount = currentProps.deal && currentProps.deal.loanCount ? currentProps.deal.loanCount : 0;

    return prevProps.deal.dealStatusId !== currentProps.deal.dealStatusId || 
    prevLoanCount !== currentLoanCount;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps !== this.props) {
      if (this.shouldUpdateDealStatus(prevProps, this.props)) this.handleDealStatusUpdate();
      let newState = { ...this.state };
      newState = this.setShouldShowModule(newState);
      newState = this.updateQuotes(newState);
      this.setState(newState);
    }
  }

  setShouldShowModule = (state) => {
    const { deal } = this.props;
    const shouldShow = !!deal && !!deal.dealStatusId;
    const found = shouldShow && !!DealStatusShownOn.some(statusId => statusId === deal.dealStatusId);
    return { ...state, shouldShowModule: found };
  }

  updateQuotes = (state) => {
    const { quotes } = this.props;
    if (!quotes) return;

    return {
      ...state
      , allQuotes: quotes
      , seniorQuotes: quotes.filter(x => x.loanSeniorityId === LOAN_SENIORITY_TYPES.SENIOR)
      , subordinateQuotes: quotes.filter(x => x.loanSeniorityId === LOAN_SENIORITY_TYPES.SUBORDINATE)
    };
  }

  handleDealStatusUpdate = () => {
    const { deal } = this.props;

    if (!deal || !deal.dealStatusId) return;

    switch (deal.dealStatusId) {
      case DEAL_STATUSES.PIPELINE:
      case DEAL_STATUSES.REFINANCE:
        this.props.quoteModuleGetQuotes({ dealId: deal.id });
        break;

      case DEAL_STATUSES.ADDING_SUPPLEMENTAL:
        this.props.quoteModuleGetSupplementalQuotes({ dealId: deal.id });
        break;

      case DEAL_STATUSES.MODIFYING:
        this.props.quoteModuleGetModifyingQuotes({ dealId: deal.id });
        break;
    }
  }

  goToQuote = (id) => {
    this.props.history.push(`/quotes/${this.props.deal.id}/${id}`);
  };

  goToQuotes = () => this.props.history.push(`/quotes/${this.props.deal.id}/new`);

  getTitle = () => {
    const { deal } = this.props;

    switch (deal.dealStatusId) {
      case DEAL_STATUSES.ADDING_SUPPLEMENTAL:
        return 'Supplemental Quotes';

      case DEAL_STATUSES.MODIFYING:
        return 'Loans in Modification';

      case DEAL_STATUSES.PIPELINE:
      case DEAL_STATUSES.REFINANCE:
      default:
        return 'Quotes';
    }
  }

  getSubTitle = () => {
    const { allQuotes, seniorQuotes, subordinateQuotes, isViewingSeniorQuotes } = this.state;
    const { deal } = this.props;

    if ((deal.dealStatusId === DEAL_STATUSES.PIPELINE || deal.dealStatusId === DEAL_STATUSES.REFINANCE) && isViewingSeniorQuotes) {
      return seniorQuotes.length > 5 ? "Showing Top 5" : "";
    } else if ((deal.dealStatusId === DEAL_STATUSES.PIPELINE || deal.dealStatusId === DEAL_STATUSES.REFINANCE) && !isViewingSeniorQuotes) {
      return subordinateQuotes.length > 5 ? "Showing Top 5" : "";
    } else {
      return allQuotes.length > 5 ? "Showing Top 5" : "";
    }    
  }

  renderFooterButton = () => {
    const { deal } = this.props
    const showDealStatuses = [DEAL_STATUSES.PIPELINE, DEAL_STATUSES.REFINANCE, DEAL_STATUSES.DEAD, DEAL_STATUSES.ADDING_SUPPLEMENTAL];
    if (!deal || !deal.dealStatusId || !showDealStatuses.some(id => id === deal.dealStatusId)) return null;

    return (
      <div style={{ height: '64px', display: 'flex', justifyContent: 'space-between' }}>
        <StyledFooterButton
          label="Show all Quotes"
          onClick={() => this.goToQuotes()}
          iconName={'arrow_forward_ios'}
        />
      </div>
    );
  }

  renderHeaderButton = () => {
    const { subordinateQuotes } = this.state;

    if (!subordinateQuotes || !subordinateQuotes.length) return null;

    return (
      <ButtonBar
        size={ButtonSizeOptions.smFluid}
        options={[
          {
            label: "Senior"
            , index: 0
            , onClick: () => this.setState({ ...this.state, isViewingSeniorQuotes: true })
          }
          , {
            label: "Subordinate"
            , index: 1
            , onClick: () => this.setState({ ...this.state, isViewingSeniorQuotes: false })
          }
        ]}
      />
    );
  }

  renderQuoteTable = () => {
    const { allQuotes, seniorQuotes, subordinateQuotes, isViewingSeniorQuotes } = this.state;
    const { deal, history } = this.props;
    let quotes;

    if ((deal.dealStatusId === DEAL_STATUSES.PIPELINE || deal.dealStatusId === DEAL_STATUSES.REFINANCE) && isViewingSeniorQuotes) {
      quotes = seniorQuotes.slice(0, 5);
    } else if ((deal.dealStatusId === DEAL_STATUSES.PIPELINE || deal.dealStatusId === DEAL_STATUSES.REFINANCE) && !isViewingSeniorQuotes) {
      quotes = subordinateQuotes.slice(0, 5);
    } else {
      quotes = allQuotes.slice(0, 5);
    }

    return <QuoteTable data={quotes} dealId={deal.id} history={history} />;
  }

  render() {
    const { quotes, quoteModuleQuoteLoading, authentication, guestPermission} = this.props;
    const { shouldShowModule } = this.state;

    const canView = !authentication.isGuest || (authentication.isGuest &&  guestPermission &&  guestPermission.pipelinePermissionId && (guestPermission.pipelinePermissionId === GUEST_PERMISSIONS.VIEW || (guestPermission.pipelinePermissionId === GUEST_PERMISSIONS.EDIT && guestPermission.pipelineAllowQuoteStatus)));
    if (quoteModuleQuoteLoading || !shouldShowModule) return null;
    if (!quotes || quotes.length === 0) return (
        <ModuleEmptyButton
          style={{ marginTop: '2em', width: '648px' }}
          label="Add a quote"
          iconName={<HomeWork />}
          onClick={() => this.goToQuote('new')}
        />
    );

    return (
        <ModuleBase>
          <DefaultModuleHeader
            headerElement={[<MaterialIcon iconName="request_quote" style={{ fontSize:'36px', color: '#C4C4C4' }} />]}
            headerTitle={this.getTitle()}
            subTitle={this.getSubTitle()}
            button={this.renderHeaderButton()}
          />

          {this.renderQuoteTable()}          
          {canView && this.renderFooterButton()}
        </ModuleBase>
    );
  }
}

const mapStateToProps = ({ quoteModule, authentication }) => ({
  ...quoteModule,
  ...quoteModule.quoteModuleReducer,
  modulePermissions: mapModulePermissions(authentication),
  authentication
});

export default compose(
  withRouter
  , connect(mapStateToProps, {
    quoteModuleGetDeal
    , quoteModuleGetQuotes
    , quoteModuleGetSupplementalQuotes
    , quoteModuleGetModifyingQuotes
  })
)(QuoteModule);
